import { Box, CircularProgress, useTheme } from "@mui/material";
import React, { useState, FC } from "react";

interface IframeContentProps {
  /**
   * Pre-signed `GET` URL for the file to be displayed.
   */
  signedUrl: URL | undefined;

  /**
   * Sandbox attribute for the iframe in which content will be displayed.
   *
   * These attributes ensure that iframe content cannot escape the iframe
   * and cause damage to the parent page.
   *
   * Do NOT add any of the following to the sandbox policy:
   * - allow-top-navigation
   * - allow-top-navigation-*
   * - allow-forms
   * - allow-modals
   *
   * Documentation: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/iframe#sandbox
   */
  sandbox?: string;
}

export const IframeContent: FC<IframeContentProps> = ({
  signedUrl,
  sandbox,
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState<boolean>(true);

  React.useEffect(() => {
    // Data changed, prepare for a reload
    setLoading(true);
  }, [signedUrl]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
      }}
    >
      {loading && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            paddingTop: theme.spacing(2),
            paddingBottom: theme.spacing(2),
          }}
        >
          <CircularProgress size="1.75rem" />
        </Box>
      )}

      {signedUrl && (
        <iframe
          sandbox={sandbox}
          src={signedUrl.toString()}
          width="100%"
          style={{
            border: 0,
            opacity: loading ? 0 : 1,
            height: loading ? 0 : "100%",
            transition: "opacity 0.5s",
          }}
          loading="lazy"
          onLoad={() => setLoading(false)}
        />
      )}
    </Box>
  );
};
